import { Headphones, MessageCircle, Search, BookOpen, Sparkles } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { UseCase } from './use-case-step'

type StyleValue = 'morning-briefing' | 'two-friends' | 'deep-dive' | 'storyteller'

const STYLES = [
  {
    value: 'morning-briefing' as const,
    label: 'Morning briefing',
    description: 'Concise, punchy, fast-paced. Like a news anchor who actually has taste.',
    icon: Headphones,
  },
  {
    value: 'two-friends' as const,
    label: 'Two friends talking',
    description: 'Casual banter between two smart people who disagree sometimes.',
    icon: MessageCircle,
  },
  {
    value: 'deep-dive' as const,
    label: 'Deep dive',
    description: 'Thorough, analytical, goes below the surface.',
    icon: Search,
  },
  {
    value: 'storyteller' as const,
    label: 'Storyteller',
    description: "Narrative-driven, builds a story arc around the week's best content.",
    icon: BookOpen,
  },
]

// Custom descriptions and recommended style by use case
const USE_CASE_CONFIG: Record<UseCase, { recommended: StyleValue; descriptions: Record<StyleValue, string>; heading: string; subheading: string }> = {
  'busy-professional': {
    heading: 'Pick the vibe',
    subheading: 'Choose the style that fits your routine. We recommend a fast-paced briefing for busy schedules.',
    recommended: 'morning-briefing',
    descriptions: {
      'morning-briefing': 'Perfect for commutes. Get all the key updates in a crisp, efficient format.',
      'two-friends': 'More relaxed listening. Great for longer drives or gym sessions.',
      'deep-dive': 'When you want to really understand a topic, not just skim it.',
      'storyteller': 'Turn your updates into an engaging narrative for more immersive listening.',
    },
  },
  'student-learner': {
    heading: 'How do you learn best?',
    subheading: 'Choose the style that helps you retain information. Deep dives work great for complex topics.',
    recommended: 'deep-dive',
    descriptions: {
      'morning-briefing': 'Quick review format. Great for revision before exams.',
      'two-friends': 'Discussion format helps you see multiple perspectives on a topic.',
      'deep-dive': 'Thorough explanations that break down complex concepts step by step.',
      'storyteller': 'Narrative format makes dry material more memorable and engaging.',
    },
  },
  'curious-explorer': {
    heading: 'How do you like to explore?',
    subheading: 'Choose the style that makes learning feel like entertainment.',
    recommended: 'storyteller',
    descriptions: {
      'morning-briefing': 'Quick hits of fascinating facts and discoveries.',
      'two-friends': 'Like overhearing two interesting people discuss your favorite topics.',
      'deep-dive': 'Satisfy your curiosity with thorough explorations of each topic.',
      'storyteller': "Transform your interests into captivating narratives you won't want to pause.",
    },
  },
}

interface StyleStepProps {
  value: StyleValue
  useCase: UseCase | null
  onChange: (value: StyleValue) => void
}

export function StyleStep({ value, useCase, onChange }: StyleStepProps) {
  const config = useCase ? USE_CASE_CONFIG[useCase] : null
  const recommendedStyle = config?.recommended || null

  return (
    <div>
      <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-foreground">
        {config?.heading || 'Pick the vibe'}
      </h2>
      <p className="mb-8 text-base font-medium text-foreground/60">
        {config?.subheading || 'Choose the style that fits how you like to listen.'}
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {STYLES.map((style) => {
          const Icon = style.icon
          const isSelected = value === style.value
          const isRecommended = recommendedStyle === style.value
          const description = config?.descriptions[style.value] || style.description

          return (
            <button
              key={style.value}
              onClick={() => onChange(style.value)}
              className={cn(
                'group relative cursor-pointer rounded-3xl border p-6 text-left transition-all',
                isSelected
                  ? 'border-brand-300 bg-brand-50/70 ring-1 ring-brand-300'
                  : 'border-black/10 bg-white hover:border-black/20 hover:bg-black/[0.02]'
              )}
            >
              {isRecommended && (
                <div className="absolute -top-3 right-5 flex items-center gap-1 rounded-full bg-brand-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-sm">
                  <Sparkles className="h-3 w-3" />
                  Recommended
                </div>
              )}
              <div className="mb-4 flex items-center gap-3">
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-2xl transition-colors',
                    isSelected ? 'bg-brand-100 text-brand-700' : 'bg-secondary-100 text-foreground/50 group-hover:bg-secondary-200'
                  )}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <span className="text-lg font-bold text-foreground">{style.label}</span>
              </div>
              <p className="text-[15px] leading-relaxed text-foreground/60">{description}</p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
